import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Link, Typography } from "@mui/material";

import { LAYERS } from "../constants";
import { formatDate, formatMoney } from "../helpers";
import { brokersById, claimsByMotor, exposuresByMotor, policiesById } from "../portfolioData";
import ClaimsSection from "./ClaimsSection";
import FieldList from "./FieldList";

function MotorDetail({ motor, language, onOpen }) {
  const { t } = useTranslation();
  const broker = brokersById[motor.broker_id];
  const exposures = exposuresByMotor[motor.id] || [];
  const claims = claimsByMotor[motor.id] || [];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {motor.registration_number}
      </Typography>
      <FieldList
        rows={[
          { label: t("portfolioExplorer.fields.vin"), value: motor.vin },
          { label: t("portfolioExplorer.fields.vehicleType"), value: t(`portfolioExplorer.enums.vehicleType.${motor.vehicle_type}`) },
          { label: t("portfolioExplorer.fields.make"), value: motor.make },
          { label: t("portfolioExplorer.fields.model"), value: motor.model },
          { label: t("portfolioExplorer.fields.registrationYear"), value: motor.registration_year },
          { label: t("portfolioExplorer.fields.fuelType"), value: t(`portfolioExplorer.enums.fuelType.${motor.fuel_type}`) },
          { label: t("portfolioExplorer.fields.engineCc"), value: motor.engine_cc ? `${motor.engine_cc} cc` : "—" },
          { label: t("portfolioExplorer.fields.powerKw"), value: `${motor.power_kw} kW` },
          { label: t("portfolioExplorer.fields.vehicleValue"), value: formatMoney(motor.vehicle_value, language) },
          { label: t("portfolioExplorer.fields.premium"), value: formatMoney(motor.premium, language) },
          { label: t("portfolioExplorer.fields.usage"), value: t(`portfolioExplorer.enums.usage.${motor.usage}`) },
          { label: t("portfolioExplorer.fields.city"), value: motor.city },
          { label: t("portfolioExplorer.fields.region"), value: motor.region },
          { label: t("portfolioExplorer.fields.country"), value: motor.country },
          { label: t("portfolioExplorer.fields.garagePostalCode"), value: motor.garage_postal_code },
        ]}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("portfolioExplorer.broker")}
      </Typography>
      {broker && (
        <Link component="button" type="button" onClick={() => onOpen(LAYERS.broker, broker.id)} sx={{ mb: 2, display: "block" }}>
          {broker.name}
        </Link>
      )}

      <ClaimsSection claims={claims} language={language} kind="motor" />

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        {t("portfolioExplorer.exposures")}
      </Typography>
      {exposures.map((exposure) => {
        const policy = policiesById[exposure.policy_id];
        return (
          <Box key={exposure.policy_id} sx={{ mb: 1.5, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{policy?.policy_number}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t(`portfolioExplorer.enums.status.${policy?.status}`)} · {t(`portfolioExplorer.enums.coverageType.${exposure.coverage_type}`)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.vehicle")}: {formatMoney(exposure.vehicle_sum_insured, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.thirdParty")}: {formatMoney(exposure.third_party_limit, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.ownDamage")}: {formatMoney(exposure.own_damage_limit, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.deductible")}: {formatMoney(exposure.deductible, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.fields.premium")}: {formatMoney(exposure.premium, language)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t("portfolioExplorer.policy.insuredPeriod")}: {formatDate(exposure.insured_from, language)} – {formatDate(exposure.insured_to, language)}
            </Typography>
            {exposure.cancelled_at && (
              <Typography variant="caption" color="text.secondary" component="div">
                {t("portfolioExplorer.policy.cancelled")}: {formatDate(exposure.cancelled_at, language)}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default MotorDetail;
